import { defineStore } from 'pinia'
import { getAppVersion, siteHasAccess, isInAccessList, isDevEnv } from '@/utils/helpers'
import type MDBUtilisateur from '@/types/utilisateur'
import type MDBSite from '@/types/site'
import type { MDBCulture, MDBCultureVariété } from '@/types/ferme/culture'
import type MDBChamp from '@/types/ferme/champ'
import type MDBBalise from '@/types/balise'
import type MDBVegcoProduit from '@/types/vegco/produit'
import type { RouteMeta } from 'vue-router'

declare interface LettreDappelSite {
  id: string
  lettresDappel: string
}

declare interface VegcoData {
  produits: MDBVegcoProduit[]
}

declare interface UtilisateurStore {
  estChargéVersion: string
  estChargéDate: number
  utilisateur: MDBUtilisateur
  annéeSélectionnée: number
  journéeSélectionnée: string
  sites: MDBSite[]
  siteSélectionné: MDBSite
  cultures: MDBCulture[]
  cultureSélectionnée: MDBCulture
  variétéSélectionnée: MDBCultureVariété
  champs: MDBChamp[]
  balises: MDBBalise[]
  lettresDappelSites: LettreDappelSite[]
  vegco: VegcoData
}

export const useUtilisateurStore = defineStore('utilisateur', {
  persist: true,
  state: () => {
    return {
      estChargéVersion: '0.0.0',
      estChargéDate: 0,
      utilisateur: {} as MDBUtilisateur,
      annéeSélectionnée: new Date().getFullYear(),
      journéeSélectionnée: '',
      sites: [] as MDBSite[],
      siteSélectionné: {} as MDBSite,
      cultures: [] as MDBCulture[],
      cultureSélectionnée: {} as MDBCulture,
      variétéSélectionnée: {} as MDBCultureVariété,
      champs:[] as MDBChamp[],
      balises:[] as MDBBalise[],
      lettresDappelSites: [] as LettreDappelSite[],
      vegco: { produits: [] as MDBVegcoProduit[] } as VegcoData
    } as UtilisateurStore
  },
  actions: {
    déconnexion() {
      this.estChargéVersion = "0.0.0"
      this.utilisateur = {} as MDBUtilisateur
      this.annéeSélectionnée = new Date().getFullYear()
      this.journéeSélectionnée = ''
      this.sites = [] as MDBSite[]
      this.siteSélectionné = {} as MDBSite
      this.cultures = [] as MDBCulture[]
      this.cultureSélectionnée = {} as MDBCulture
      this.variétéSélectionnée = {} as MDBCultureVariété
      this.champs = [] as MDBChamp[]
      this.balises = [] as MDBBalise[]
      this.lettresDappelSites = [] as LettreDappelSite[]
      this.vegco = { produits: [] as MDBVegcoProduit[] } as VegcoData
    },

    getVersion() : string { return getAppVersion() },

    estChargé() {
      return this.estChargéVersion === this.getVersion() &&
        this.estChargéDate > Date.now() - 1000 * 60 * 60 * 12
    },

    aAccès(meta: RouteMeta | undefined) {
      if (meta == undefined || !meta.requiresAuth)
        return true

      if (meta.isDev && !isDevEnv())
        return false

      if (!this.estChargé())
        return false

      if ((meta.requiresAdmin ?? false) && !this.utilisateur.estAdministrateur)
        return false;

      if (meta.requiresSiteAccess && !this.siteAAccès(meta.requiresSiteAccess as string))
        return false;

      if (meta.id && this.utilisateur.pagesListeNoire &&
        this.utilisateur.pagesListeNoire.includes(meta.id as string))
        return false;

      if (meta.id && this.utilisateur.pagesListeBlanche &&
        this.utilisateur.pagesListeBlanche.length > 0 &&
        !this.utilisateur.pagesListeBlanche.includes(meta.id as string))
        return false;

      return true
    },

    siteAAccès(access: string) {
      return siteHasAccess(this.siteSélectionné, access)
    },

    aAccèsPermission(access: string) {
      if (this.utilisateur.estAdministrateur)
        return true

      return !isInAccessList(this.utilisateur.accèsNon, access) &&
        isInAccessList(this.utilisateur.accès, access)
    },

    sélectionnerAnnée(année: number) {
      const maxAnnée = new Date().getFullYear() + 1
      if (année >= 2024 && année <= maxAnnée) {
        this.annéeSélectionnée = année
      } else {
        this.annéeSélectionnée = new Date().getFullYear()
      }

      this.journéeSélectionnée = new Date(this.annéeSélectionnée, new Date().getMonth(), new Date().getDate()).toISOString()
    },

    async sélectionnerSite(nomSite: string) {
      if (this.siteSélectionné.nom === nomSite)
        return

      this.siteSélectionné = {} as MDBSite
      this.cultures = [] as MDBCulture[]
      this.cultureSélectionnée = {} as MDBCulture
      this.variétéSélectionnée = {} as MDBCultureVariété
      this.champs = [] as MDBChamp[]
      this.balises = [] as MDBBalise[]
      this.vegco = { produits: [] as MDBVegcoProduit[] } as VegcoData

      const site = this.sites.find(site => site.nom === nomSite)
      if (site) {
        this.siteSélectionné = site

        const siteCS = this.siteSélectionné.lettresDappel

        if (!this.lettresDappelSites.find(site => site.lettresDappel === siteCS)) {
          this.lettresDappelSites.push({ id: site.id, lettresDappel: siteCS })
        }

        const response = await apiProphet.get(`sites/${siteCS}/cultures`)
        const cultures = response.data as MDBCulture[]
        this.cultures = cultures.sort((a, b) => a.nom.localeCompare(b.nom))
        this.cultureSélectionnée = {} as MDBCulture
        this.variétéSélectionnée = {} as MDBCultureVariété

        const response2 = await apiProphet.get(`sites/${siteCS}/champs`)
        const champs = response2.data as MDBChamp[]
        this.champs = champs.sort((a, b) => a.nom.localeCompare(b.nom))

        this.balises = [] as MDBBalise[]
        this.vegco = { produits: [] as MDBVegcoProduit[] } as VegcoData

        this.sélectionnerAnnée(new Date().getFullYear())
        this.sélectionnerCulture(this.utilisateur.paramètresSession.cultureChoisie,
          this.utilisateur.paramètresSession.variétéChoisie)
      }
    },

    sélectionnerCulture(nomCulture: string, nomVariété: string | null | undefined) {
      this.variétéSélectionnée = {} as MDBCultureVariété
      const culture = this.cultures.find(culture => culture.nom === nomCulture)
      if (culture) {
        this.cultureSélectionnée = culture
        if (nomVariété) {
          const variété = this.cultureSélectionnée.variétés.find(variété => variété.nom === nomVariété)
          if (variété) {
            this.variétéSélectionnée = variété
          }
        } else if (this.cultureSélectionnée.variétés.length > 0) {
          this.variétéSélectionnée = this.cultureSélectionnée.variétés[0]
        }
      } else {
        this.cultureSélectionnée = {} as MDBCulture
      }

      const c = this.cultureSélectionnée?.nom ?? ''
      const v = this.variétéSélectionnée?.nom ?? ''

      if (this.utilisateur.paramètresSession.cultureChoisie !== c || this.utilisateur.paramètresSession.variétéChoisie !== v) {
        this.utilisateur.paramètresSession.cultureChoisie = c
        this.utilisateur.paramètresSession.variétéChoisie = v
        this.updateParamètresSession()
      }
    },

    async updateParamètresSite() {
      await apiProphet.put('api/utilisateur/site', this.utilisateur.paramètresSite)
    },

    async updateParamètresSession() {
      await apiProphet.put('api/utilisateur/session', this.utilisateur.paramètresSession)
    },

    async updateParamètresAlertes() {
      await apiProphet.put('api/utilisateur/alertes', this.utilisateur.paramètresAlertes)
    },

    async fetchBalisesIfNeeded() {
      if (this.balises.length !== 0)
        return

      this.balises = [] as MDBBalise[]

      const siteCS = this.siteSélectionné.lettresDappel
      const result = await apiProphet.get(`sites/${siteCS}/balises`)
      this.balises = result.data as MDBBalise[]

      const groupeCS = this.siteSélectionné.siteGroupeLD
      if (groupeCS !== "") {
        const result2 = await apiProphet.get(`sites/${groupeCS}/balises`)
        const newBalises = result2.data as MDBBalise[]
        this.balises = this.balises.concat(newBalises.filter(balise => {
          return !this.balises.some(b => b.id === balise.id)
        }))
      }
    },

    async fetchVegcoProduitsIfNeeded() {
      if (this.vegco.produits === undefined)
        this.vegco.produits = [] as MDBVegcoProduit[]

      if (this.vegco.produits.length !== 0)
        return

      const groupeCS = this.siteSélectionné.siteGroupeLD
      if (groupeCS !== "") {
        const result2 = await apiProphet.get(`vgco/produits`)
        this.vegco.produits = this.vegco.produits.concat(result2.data as MDBVegcoProduit[])
      }
    },

    getVegcoProduit(codeProduit: string | undefined) {
      if (codeProduit === undefined || codeProduit === '')
        return undefined

      if (this.vegco.produits === undefined)
        this.vegco.produits = [] as MDBVegcoProduit[]

      return this.vegco.produits.find(p => p.codeProduit === codeProduit)
    },

    getBalises(type: string, catégorieType: string | null, catégorie: string | null,
      sousCatégorieType: string | null, sousCatégorie: string | null) {
      return this.balises
        .filter(b => b.type == type &&
          (b.catégorie == null || (b.catégorieType == catégorieType && b.catégorie == catégorie)) &&
          (b.sousCatégorie == null || (b.sousCatégorieType == sousCatégorieType && b.sousCatégorie == sousCatégorie)))
    },

    getSiteLettresDappel(siteId: string | undefined) {
      if (siteId === undefined || siteId === '')
        return ''
      const site = this.lettresDappelSites.find(site => site.id === siteId)
      if (site)
        return site.lettresDappel
      return ''
    },

    async getOrFetchSiteLettresDappel(siteId: string) {
      const site = this.lettresDappelSites.find(site => site.id === siteId)
      if (site)
        return site.lettresDappel

      const result = await apiProphet.get(`sites/${siteId}/lettresDappel`)
      const lettresDappel = result.data as string
      this.lettresDappelSites.push({ id: siteId, lettresDappel })
      return lettresDappel
    },

    getSiteId(lettresDappel: string) {
      const site = this.lettresDappelSites.find(site => site.lettresDappel === lettresDappel)
      return site?.id
    },

    async getOrFetchSiteId(lettresDappel: string) {
      const site = this.lettresDappelSites.find(site => site.lettresDappel === lettresDappel)
      if (site)
        return site.id

      const result = await apiProphet.get(`sites/${lettresDappel}/id`)
      const id = result.data as string
      this.lettresDappelSites.push({ id: id, lettresDappel })
      return id
    },

    createVegcoSiteSettingsIfNeeded() {
      if (this.utilisateur.paramètresSite.vegco == null || this.utilisateur.paramètresSite.vegco == undefined) {
        this.utilisateur.paramètresSite.vegco = {
          afficherCommandesNonLivréesDuPassé: false
        }
      }
    }
  }
})
